/**
 * Balance React Query Hooks
 *
 * Provides data fetching hooks for household balance and expense allocation queries.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { balanceService, type BalanceParams } from '../services/balance-service';

/**
 * Query key factory for balance queries
 */
export const balanceKeys = {
  all: ['balances'] as const,
  summary: (params?: BalanceParams) => [...balanceKeys.all, 'summary', params] as const,
  users: (params?: BalanceParams) => [...balanceKeys.all, 'users', params] as const,
  user: (userId: string, params?: BalanceParams) =>
    [...balanceKeys.all, 'user', userId, params] as const,
  settlements: (params?: BalanceParams) =>
    [...balanceKeys.all, 'settlementSuggestions', params] as const,
  activity: (params?: BalanceParams) =>
    [...balanceKeys.all, 'activity', params] as const,
  rolloverLedger: (memberId?: string) =>
    [...balanceKeys.all, 'rolloverLedger', memberId] as const,
  unsettledTransactions: (params?: BalanceParams) =>
    [...balanceKeys.all, 'unsettledTransactions', params] as const,
  settlementPairs: (params?: BalanceParams) =>
    [...balanceKeys.all, 'settlementPairs', params] as const,
  pairUnsettledTransactions: (otherUserId: string, params?: BalanceParams) =>
    [...balanceKeys.all, 'pairUnsettledTransactions', otherUserId, params] as const,
};

/**
 * Hook to fetch balance summary for all household members
 *
 * @param params - Optional date range parameters
 * @returns Query result with balance summary
 */
export function useBalanceSummary(params?: BalanceParams) {
  return useQuery({
    queryKey: balanceKeys.summary(params),
    queryFn: () => balanceService.getSummary(params),
    staleTime: 30 * 1000, // High volatility — balances change with transactions
  });
}

/**
 * Hook to fetch balances for all users
 *
 * @param params - Optional date range parameters
 * @returns Query result with user balances
 */
export function useUserBalances(params?: BalanceParams) {
  return useQuery({
    queryKey: balanceKeys.users(params),
    queryFn: () => balanceService.getUserBalances(params),
  });
}

/**
 * Hook to fetch balance for a specific user
 *
 * @param userId - User ID (query is disabled if undefined)
 * @param params - Optional date range parameters
 * @returns Query result with user balance
 */
export function useUserBalance(userId: string | undefined, params?: BalanceParams) {
  return useQuery({
    queryKey: balanceKeys.user(userId!, params),
    queryFn: () => balanceService.getUserBalance(userId!, params),
    enabled: !!userId,
  });
}

/**
 * Hook to fetch suggested settlement transactions
 *
 * @param params - Optional date range parameters
 * @returns Query result with settlement suggestions
 */
export function useSettlementSuggestions(params?: BalanceParams) {
  return useQuery({
    queryKey: balanceKeys.settlements(params),
    queryFn: () => balanceService.getSettlementSuggestions(params),
  });
}

/**
 * Hook to fetch paginated activity feed (expenses + settlements)
 *
 * Uses infinite query for "Load more" UX.
 */
export function useInfiniteActivity(params?: BalanceParams) {
  return useInfiniteQuery({
    queryKey: balanceKeys.activity(params),
    queryFn: ({ pageParam }) =>
      balanceService.getActivity({ ...params, page: pageParam as number, page_size: 20 }),
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.total / lastPage.page_size);
      return lastPage.page < totalPages ? lastPage.page + 1 : undefined;
    },
    initialPageParam: 1,
  });
}

/**
 * Hook to fetch unsettled transactions for the current user via allocations.
 *
 * Uses infinite query for "Load more" pagination.
 */
export function useUnsettledTransactions(params?: BalanceParams) {
  return useInfiniteQuery({
    queryKey: balanceKeys.unsettledTransactions(params),
    queryFn: ({ pageParam }) =>
      balanceService.getUnsettledTransactions({
        ...params,
        page: pageParam as number,
        page_size: 20,
      }),
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.total / lastPage.page_size);
      return lastPage.page < totalPages ? lastPage.page + 1 : undefined;
    },
    initialPageParam: 1,
  });
}

/**
 * Hook to fetch settlement-focused rollover ledger with per-pair balance history
 */
export function useRolloverLedger(memberId?: string) {
  return useQuery({
    queryKey: balanceKeys.rolloverLedger(memberId),
    queryFn: () => balanceService.getRolloverLedger(memberId),
  });
}

/**
 * Hook to fetch bilateral balances between current user and each household member
 */
export function useSettlementPairs(params?: BalanceParams) {
  return useQuery({
    queryKey: balanceKeys.settlementPairs(params),
    queryFn: () => balanceService.getSettlementPairs(params),
  });
}

/**
 * Hook to fetch unsettled transactions between current user and a specific other user
 */
export function usePairUnsettledTransactions(
  otherUserId: string | undefined,
  params?: BalanceParams
) {
  return useQuery({
    queryKey: balanceKeys.pairUnsettledTransactions(otherUserId!, params),
    queryFn: () => balanceService.getPairUnsettledTransactions(otherUserId!, params),
    enabled: !!otherUserId,
  });
}
